import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import { useSelectedCountry } from '../../shared/hooks/useSelectedCountry';
import { usePublicRequests } from './useRequests';
import { RequestCard } from './RequestCard';
import type { CommunityRequest } from '../../shared/services/requests.service';

type SortKey = 'newest' | 'mostResponses' | 'expiringSoon';
type FilterKey = 'all' | 'withResponses' | 'expiringSoon';

/**
 * Public feed of community requests, filtered by the selected country plus
 * an optional city-name substring. Client-side sort + quick filter chips
 * since the current page size (20) is small enough to handle in-memory
 * without a server round-trip.
 */
export function RequestsListPage() {
  const { t } = useTranslation();
  const { countryCode } = useSelectedCountry();
  const [cityFilter, setCityFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [quickFilter, setQuickFilter] = useState<FilterKey>('all');

  const { requests, isLoading } = usePublicRequests({
    countryCode: (countryCode || '').toUpperCase() || undefined,
    cityName: cityFilter.trim() || undefined,
  });

  // Derived stats — bare numbers but the hero feels meaningless without them.
  const totalResponders = useMemo(
    () => requests.reduce((sum, r) => sum + r.responseCount, 0),
    [requests],
  );

  const visible: CommunityRequest[] = useMemo(() => {
    const now = Date.now();
    const expiringSoonWindowMs = 3 * 24 * 60 * 60 * 1000;
    let list = requests;
    if (quickFilter === 'withResponses') {
      list = list.filter((r) => r.responseCount > 0);
    } else if (quickFilter === 'expiringSoon') {
      list = list.filter((r) => new Date(r.expiresAt).getTime() - now < expiringSoonWindowMs);
    }
    const sorted = [...list];
    if (sortKey === 'mostResponses') {
      sorted.sort((a, b) => b.responseCount - a.responseCount);
    } else if (sortKey === 'expiringSoon') {
      sorted.sort(
        (a, b) => new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime(),
      );
    } else {
      sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    return sorted;
  }, [requests, quickFilter, sortKey]);

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* HERO — gradient panel with title, stats and the primary CTA. The
          accent gradient sits behind the title to give the page some
          presence; everything below stays on the neutral page background. */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main}14 0%, ${theme.palette.secondary.main}14 100%)`,
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems={{ md: 'flex-end' }}
            justifyContent="space-between"
          >
            <Box>
              <Chip
                icon={<ForumRoundedIcon />}
                label={t('requests.heroEyebrow')}
                size="small"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  fontWeight: 700,
                  mb: 1.5,
                  '& .MuiChip-icon': { color: 'common.white' },
                }}
              />
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 1, lineHeight: 1.1 }}>
                {t('requests.listTitle')}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ maxWidth: 600, fontSize: { xs: '1rem', md: '1.1rem' } }}
              >
                {t('requests.heroSubtitle')}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/requests/new"
              variant="contained"
              size="large"
              startIcon={<AddRoundedIcon />}
              sx={{
                alignSelf: { xs: 'stretch', md: 'flex-end' },
                py: 1.5,
                px: 3,
                fontWeight: 700,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}
            >
              {t('requests.postAction')}
            </Button>
          </Stack>

          {/* Stat row — visible only when there's something to brag about,
              and stays hidden during the loading skeleton. */}
          {!isLoading && requests.length > 0 && (
            <Stack
              direction="row"
              spacing={{ xs: 3, md: 5 }}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: { xs: 3, md: 4 } }}
            >
              <StatTile
                icon={<ForumRoundedIcon />}
                value={requests.length}
                label={t('requests.statActiveRequests', { count: requests.length })}
              />
              <StatTile
                icon={<GroupsRoundedIcon />}
                value={totalResponders}
                label={t('requests.statTotalResponders', { count: totalResponders })}
              />
            </Stack>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        {/* TOOLBAR — search, quick filters, sort. Wrapped in a Paper for
            visual grouping; sits flush above the grid. */}
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                placeholder={t('requests.cityFilterPlaceholder')}
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                sx={{ minWidth: { md: 220 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SortRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="newest">{t('requests.sortNewest')}</MenuItem>
                <MenuItem value="mostResponses">{t('requests.sortMostResponses')}</MenuItem>
                <MenuItem value="expiringSoon">{t('requests.sortExpiringSoon')}</MenuItem>
              </TextField>
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <FilterChip
                active={quickFilter === 'all'}
                onClick={() => setQuickFilter('all')}
                label={t('requests.filterAll')}
              />
              <FilterChip
                active={quickFilter === 'withResponses'}
                onClick={() => setQuickFilter('withResponses')}
                label={t('requests.filterWithResponses')}
              />
              <FilterChip
                active={quickFilter === 'expiringSoon'}
                onClick={() => setQuickFilter('expiringSoon')}
                label={t('requests.filterExpiringSoon')}
              />
            </Stack>
          </Stack>
        </Paper>

        {/* RESULTS */}
        {isLoading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : visible.length === 0 ? (
          <EmptyState
            hasUnderlyingResults={requests.length > 0}
            onResetFilters={() => {
              setCityFilter('');
              setQuickFilter('all');
            }}
          />
        ) : (
          <Grid container spacing={2.5}>
            {visible.map((r) => (
              <Grid item xs={12} sm={6} md={4} key={r.id}>
                <RequestCard request={r} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}

function StatTile({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
          color: 'primary.main',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <Chip
      label={label}
      onClick={onClick}
      sx={{
        fontWeight: active ? 700 : 500,
        bgcolor: active ? 'primary.main' : 'action.hover',
        color: active ? 'common.white' : 'text.primary',
        border: '1px solid',
        borderColor: active ? 'primary.main' : 'divider',
        '&:hover': {
          bgcolor: active ? 'primary.dark' : 'action.selected',
        },
      }}
    />
  );
}

function EmptyState({
  hasUnderlyingResults,
  onResetFilters,
}: {
  hasUnderlyingResults: boolean;
  onResetFilters: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: { xs: 6, md: 9 },
        px: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 4,
      }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          mx: 'auto',
          mb: 2.5,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main}1F 0%, ${theme.palette.secondary.main}1F 100%)`,
          color: 'primary.main',
        }}
      >
        <SearchOffRoundedIcon sx={{ fontSize: 44 }} />
      </Box>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
        {hasUnderlyingResults ? t('requests.emptyFiltered') : t('requests.emptyFeed')}
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 460, mx: 'auto', mb: 3 }}>
        {hasUnderlyingResults ? t('requests.emptyFilteredHint') : t('requests.emptyFeedHint')}
      </Typography>
      {hasUnderlyingResults ? (
        <Button variant="outlined" onClick={onResetFilters}>
          {t('requests.clearFilters')}
        </Button>
      ) : (
        <Button
          component={RouterLink}
          to="/requests/new"
          variant="contained"
          startIcon={<AddRoundedIcon />}
        >
          {t('requests.postAction')}
        </Button>
      )}
    </Box>
  );
}
